import { GitBranchIcon, PulseIcon } from '@phosphor-icons/react';

import type { AgentSession } from '@/api-models/agent-sessions';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';
import { formatTimeAgo } from '@/utils/format';

import { sessionTitle, type SessionGroup } from './session-controls';
import { SessionActionsMenu } from './SessionActionsMenu';
import { AwaitingAnswerChip } from './AwaitingAnswerChip';
import { sessionPhase } from './session-status';
import { SessionStatusPill } from './SessionStatusPill';

interface SessionsListProps {
  groups: SessionGroup[];
  /** When false, render a single flat list without group headers. */
  grouped: boolean;
  isLoading: boolean;
  /** True when the team has sessions but none match the current view. */
  isFiltered: boolean;
  onOpenSession: (id: string) => void;
  /** `repository_id` → repo label, so each row says which repository it works on. */
  repoLabel: (repositoryId: string) => string;
  /** Re-fetch the list after an archive/delete. */
  onSessionsChanged: () => void;
}

/** Grouped, sticky-header rows for a team's sessions. */
export function SessionsList({
  groups,
  grouped,
  isLoading,
  isFiltered,
  onOpenSession,
  repoLabel,
  onSessionsChanged,
}: SessionsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={6} />
      </div>
    );
  }

  if (groups.every((g) => g.sessions.length === 0)) {
    return isFiltered ? (
      <EmptyState icon={PulseIcon} title="No matches" description="No sessions match this view." />
    ) : (
      <EmptyState
        icon={PulseIcon}
        title="No sessions yet"
        description="Start a session to put an agent to work on a repository."
      />
    );
  }

  return (
    <div className="flex flex-col gap-1 px-2 py-2">
      {groups.map((group) => (
        <section key={group.key} className="flex flex-col">
          {grouped && (
            <div className="sticky top-0 z-5 flex h-8.5 items-center gap-2 rounded-md bg-sunken/95 px-3 backdrop-blur-sm">
              <span className="size-2 rounded-full" style={{ backgroundColor: group.color }} aria-hidden />
              <span className="text-2xs font-semibold text-fg">{group.label}</span>
              <span className="text-2xs tabular-nums text-fg-tertiary">{group.sessions.length}</span>
            </div>
          )}
          <div className="flex flex-col">
            {group.sessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                repo={repoLabel(session.repository_id)}
                onClick={() => onOpenSession(session.id)}
                onChanged={onSessionsChanged}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

interface SessionRowProps {
  session: AgentSession;
  repo: string;
  onClick: () => void;
  onChanged: () => void;
}

function SessionRow({ session, repo, onClick, onChanged }: SessionRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group flex h-11.5 cursor-pointer items-center gap-2.5 rounded-lg px-3 outline-none transition-colors hover:bg-surface-2 focus-visible:bg-surface-2"
    >
      <span className="min-w-0 flex-1 truncate text-xs text-fg">{sessionTitle(session)}</span>

      <div className="flex shrink-0 items-center gap-2">
        {session.archived_at && (
          <span className="rounded-full bg-surface-3 px-2 py-0.5 text-2xs font-semibold text-fg-tertiary">
            Archived
          </span>
        )}

        <span className="hidden max-w-50 truncate text-3xs text-fg-tertiary sm:inline" title={repo}>
          {repo}
        </span>

        {session.branch && (
          <span className="hidden max-w-45 items-center gap-1.5 rounded-full bg-surface-3 px-2 font-mono text-3xs text-fg-secondary md:inline-flex">
            <GitBranchIcon size={11} className="shrink-0" />
            <span className="truncate">{session.branch}</span>
          </span>
        )}

        <span className="w-11 text-right text-3xs tabular-nums text-fg-tertiary">
          {formatTimeAgo(session.updated_at)}
        </span>

        <AwaitingAnswerChip session={session} />

        <SessionStatusPill phase={sessionPhase(session)} />

        <SessionActionsMenu session={session} onChanged={onChanged} />
      </div>
    </div>
  );
}
